import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { classNames } from '@panwds/tailwindcss';
import React from 'react';
import { useIntl } from 'react-intl';
import { useTableInstance } from '../TableContext/index.js';
import { useTableLayoutEnabled } from '../TableLayout/index.js';
import { Cell, CellWrapper } from './Cell.js';
import { DragHandle } from './DragHandle.js';
function groupCells(cells) {
    return cells.reduce((acc, cell) => {
        if (cell.column.isSortRowColumn) {
            acc.sortRowCells.push(cell);
        }
        else if (cell.column.isSelectRowColumn) {
            acc.selectRowCells.push(cell);
        }
        else if (cell.isGrouped || cell.isPlaceholder) {
            acc.groupedCells.push(cell);
        }
        else if (cell.column.sticky) {
            if (cell.column.stickyPosition === 'left') {
                acc.leftStickyCells.push(cell);
            }
            else {
                acc.rightStickyCells.push(cell);
            }
        }
        else {
            acc.generalCells.push(cell);
        }
        return acc;
    }, {
        sortRowCells: [],
        selectRowCells: [],
        groupedCells: [],
        generalCells: [],
        leftStickyCells: [],
        rightStickyCells: []
    });
}
export function Row({ appearance = 'primary', rowSize = 'standard', shouldHighlightRow, shouldDisableRow, row, isDragging, isHeld, sortRef, dragHandleProps, style, ...rest }) {
    const intl = useIntl();
    const { sortRowCells, selectRowCells, groupedCells, leftStickyCells, generalCells, rightStickyCells } = groupCells(row.cells);
    const instance = useTableInstance();
    const globalFilter = instance?.state?.globalFilter ?? '';
    const rowHeightClassNames = {
        standard: 'tw-h-8',
        cozy: 'tw-h-11',
        comfy: 'tw-h-24'
    };
    const shouldDisable = shouldDisableRow?.({ row }) ?? false;
    const shouldHighlight = shouldHighlightRow?.({ row }) ?? false;
    const className = classNames('tw-group', appearance !== 'secondary' && rowHeightClassNames[rowSize], isDragging && 'tw-opacity-50', isHeld && 'tw-shadow-lg', shouldDisable
        ? 'tw-text-disabled dark:tw-text-disabled-dark tw-cursor-not-allowed tw-pointer-events-none'
        : 'tw-text-primary dark:tw-text-primary-dark', 'hover:tw-bg-blue-200 dark:hover:tw-bg-gray-800 active:tw-bg-blue-100 dark:active:tw-bg-gray-900', shouldHighlight && 'tw-bg-blue-200 dark:tw-bg-blue-900', !shouldHighlight && row.isSelected && 'tw-bg-blue-100 dark:tw-bg-gray-900', !shouldHighlight && row.isSelected && appearance === 'primary' && 'even:tw-bg-blue-100 dark:even:tw-bg-gray-900', !shouldHighlight && !row.isSelected && appearance !== 'tertiary' && 'tw-bg-white dark:tw-bg-gray-1050', !shouldHighlight && !row.isSelected && appearance === 'primary' && 'even:tw-bg-gray-50 dark:even:tw-bg-gray-1000', !shouldHighlight &&
        !row.isSelected &&
        appearance === 'tertiary' &&
        (row.subRows.length > 0 ? 'tw-bg-gray-50 dark:tw-bg-gray-1000' : 'tw-bg-white dark:tw-bg-gray-900'), 'tw-border-solid', appearance === 'primary' && 'tw-border-l tw-border-r tw-border-t-0 tw-border-b-0 tw-border-transparent', appearance === 'secondary' &&
        'tw-border-l tw-border-r tw-border-t-0 tw-border-b last:tw-border-b-0 tw-border-b-gray-300 tw-border-x-transparent dark:tw-border-gray-950', appearance === 'tertiary' &&
        classNames('tw-border-0 tw-border-b tw-border-gray-300', row.subRows.length > 0 ? 'dark:tw-border-gray-800' : 'dark:tw-border-gray-700'));
    const tableLayoutEnabled = useTableLayoutEnabled();
    const { style: styleProps, ...rowProps } = row.getRowProps();
    const combinedStyles = {
        minHeight: '2rem',
        ...style,
        ...styleProps
    };
    if (tableLayoutEnabled) {
        combinedStyles.minWidth = combinedStyles.width;
        combinedStyles.width = undefined;
    }
    return (React.createElement("div", { id: `row-${row.index}`, "aria-rowindex": row.index, ...rowProps, ref: sortRef, style: combinedStyles, className: className, ...rest },
        !!selectRowCells.length && (React.createElement("div", { className: "tw-flex" }, selectRowCells.map((cell) => (React.createElement(Cell, { cell: cell, key: cell.column.id, row: row, column: cell.column }))))),
        !!groupedCells.length && (React.createElement("div", { className: "tw-flex" }, groupedCells.map((cell) => (React.createElement(Cell, { cell: cell, key: cell.column.id, row: row, column: cell.column }))))),
        !!leftStickyCells.length && (React.createElement("div", { style: {
                background: 'inherit',
                position: 'sticky',
                left: 0,
                zIndex: 1
            }, className: "tw-flex tw-shadow-left dark:tw-shadow-dark-left" }, leftStickyCells.map((cell) => (React.createElement(Cell, { cell: cell, key: cell.column.id, row: row, column: cell.column }))))),
        React.createElement("div", { className: "tw-flex tw-flex-grow" }, generalCells.map((cell, index) => (React.createElement(Cell, { cell: cell, column: cell.column, key: cell.column.id, row: row, last: generalCells.length === index + 1 })))),
        !!rightStickyCells.length && (React.createElement("div", { style: {
                background: 'inherit',
                position: 'sticky',
                right: 0
            }, className: "tw-flex tw-shadow-right dark:tw-shadow-dark-right" }, rightStickyCells.map((cell) => (React.createElement(Cell, { cell: cell, key: cell.column.id, row: row, column: cell.column }))))),
        !!sortRowCells.length && (React.createElement("div", { className: "tw-flex" }, sortRowCells.map((cell) => (React.createElement(CellWrapper, { cell: cell, key: cell.column.id },
            React.createElement(DragHandle, { ...dragHandleProps, label: intl.formatMessage({
                    defaultMessage: 'Move Row',
                    id: 'o+pYwd',
                    description: 'Drag icon user can click on to reorder rows'
                }), isHeld: isHeld, disabled: globalFilter &&
                    intl.formatMessage({
                        defaultMessage: 'Unable to sort while filtering',
                        id: 'GOKfIG',
                        description: 'Drag icon disabled tooltip'
                    }) }))))))));
}
export function SortableRow(props) {
    const instance = useTableInstance();
    const globalFilter = instance?.state?.globalFilter ?? '';
    const { isDragging, attributes, listeners, setNodeRef, transform, transition } = useSortable({
        id: String(props.row.id),
        disabled: !!globalFilter
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition
    };
    return (React.createElement(Row, { style: style, ...props, sortRef: setNodeRef, isDragging: isDragging, dragHandleProps: { ...attributes, ...listeners } }));
}
//# sourceMappingURL=Row.js.map